import { useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { translateError } from "@/common/utils/errorHandling";
import { orgQueryKeys } from "@/global-settings/data/orgQueryKeys";
import { APIEmptyResponse, ErrorResponse } from "@reflag/shared/api";

export function useAPIKeyDeleteMutation() {
  const { org } = useEnvironment();
  const queryClient = useQueryClient();
  const toast = useToast();
  const errorToast = useErrorToast();

  return useMutation<
    APIEmptyResponse,
    AxiosError<ErrorResponse>,
    { apiKeyId: string }
  >({
    mutationFn: ({ apiKeyId }) =>
      api
        .delete<"/orgs/:orgId/api-keys/:apiKeyId">(
          `/orgs/${org.id}/api-keys/${apiKeyId}`,
        )
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orgQueryKeys.apiKeys(org.id),
      });

      toast({
        title: "API key deleted",
        status: "success",
      });
    },
    onError: (error) => {
      const { title, description } = translateError(
        error,
        `Failed to delete API key`,
      );

      errorToast({
        title,
        description,
        duration: 5000,
      });
    },
  });
}
